import { ChangeDetectorRef, Component, DoCheck, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { Subscription } from 'rxjs';
import { TextboxConfig } from '../../../design-system/textbox/textbox.modal';
import { TextboxComponent } from '../../../design-system/textbox/textbox.component';
import { TwoDigitDecimalNumberDirective } from '../../../../_directives/twoDigitDecimalNumber.directive';
import { MoneyDecimalNumberDirective } from '../../../../_directives/moneyDecimalNumber.directive';
import { IntizerOnlyDirective } from '../../../../_directives/intizerOnly.directive';
import { SixDigitAfterAndBeforeDecimalNumberDirective } from '../../../../_directives/sixDigitBeforeAndAfterDecimalNumber';
import { SharedModule } from '../../../../shared.module';
import { DynamicFormService } from '../../dynamic-form/dynamic-form.service';
import { formatAmountInputWithSeparators } from '../../../../_helpers/number-format.utils';
import { stripSeparatorsPreservingCaret } from '../../../../_helpers/money-input-caret.utils';

@Component({
  selector: 'formly-input',
  templateUrl: "./formly-input.component.html",
  standalone: true,
  imports: [TextboxComponent, ReactiveFormsModule, FormlyModule, TwoDigitDecimalNumberDirective, MoneyDecimalNumberDirective, IntizerOnlyDirective, SixDigitAfterAndBeforeDecimalNumberDirective, SharedModule]
})
export class FormlyInputComponent extends FieldType implements OnInit, OnDestroy, DoCheck {

  constructor(private cdRef: ChangeDetectorRef, private dynamicFormService: DynamicFormService) { super(); }

  isFormSubmitted: boolean = false;
  private formSubmissionSubscription: Subscription;
  private originalHelperText: string | null = null;

  private valueChangeSub: any;
  @ViewChild(TextboxComponent) textbox?: TextboxComponent;
  fieldKey: string = '';
  textboxConfig: TextboxConfig = {
    id: '',
    label: '',
    placeholder: '',
    currencyText: null,
    disabled: true,
    required: false,
    size: 'md',
    rightSideText: null,
    helperText: null,
  };

  ngOnInit() {
    if (this.field.key)
      this.fieldKey = this.field.id ?? this.field.key.toString();
    this.textboxConfig.id = this.fieldKey;

    this.formSubmissionSubscription = this.dynamicFormService.getFormSubmittedEvent().subscribe(data => {
      this.isFormSubmitted = data;
      this.syncErrorState();
      this.cdRef.detectChanges();
    });

    if (this.field.templateOptions) {
      this.textboxConfig.label = this.field.templateOptions.label;
      this.textboxConfig.disabled = this.field.templateOptions.disabled;
      this.textboxConfig.required = this.field.templateOptions.required;
      this.textboxConfig.size = this.field.templateOptions.size;
      this.textboxConfig.readonly = this.field.templateOptions.readonly;
      this.textboxConfig.maxLength = this.field.templateOptions.maxLength;
      this.textboxConfig.maxLengthCount = this.field.templateOptions.maxLengthCount;
      this.textboxConfig.rightSideText = this.field.templateOptions.rightSideText;
      this.textboxConfig.rightSideTextClass = this.field.templateOptions.rightSideTextClass;
      this.textboxConfig.currencyText = this.field.templateOptions.currencyText;
      this.textboxConfig.helperText = this.field.templateOptions.helperText;
      this.originalHelperText = this.field.templateOptions.helperText ?? null;
      this.textboxConfig.placeholder = this.field.templateOptions.placeholder;
      this.textboxConfig.iconSrc = this.field.templateOptions.iconSrc;
      this.textboxConfig.iconPosition = this.field.templateOptions.iconPosition;

    }

    if (this.field.templateOptions?.turnToCaps) {
      this.formControl.valueChanges.subscribe(value => {
        if (value && value !== value.toUpperCase()) {
          this.formControl.setValue(value.toUpperCase(), { emitEvent: false });
        }
      });
    }

    if (this.field && this.field.templateOptions && typeof this.field.templateOptions.change === 'function') {
      this.valueChangeSub = this.formControl.valueChanges.subscribe(value => {
        (this.field.templateOptions.change as Function)(this.field, value);
      });
    }
  }

  OnValueChange(val: string) {
    this.to.change(this.field, val);
  }

  /**
   * Money fields drop their thousands separators while focused so the user edits a
   * plain number — grouping is re-applied in onBlur(). The caret stays on the digit
   * the user clicked; see the helper for why the timing is deferred.
   */
  onFocus(): void {
    if (!this.field.props?.['formatAsAmount']) { return; }
    stripSeparatorsPreservingCaret(
      this.textbox?.inputElement?.nativeElement,
      raw => this.setValueAndSyncModel(raw)
    );
  }

  onBlur(): void {
    if (this.field.props?.['trimStart']) { this.applyTrimStart(); }
    if (!this.field.props?.['formatAsAmount']) { return; }
    const v = this.formControl.value;
    // Strip separators before the numeric test — the value may already be formatted.
    if (v == null || v === '' || isNaN(Number(String(v).replace(/,/g, '')))) { return; }
    this.setValueAndSyncModel(formatAmountInputWithSeparators(v));
  }

  private applyTrimStart(): void {
    const value = this.formControl.value;
    if (typeof value !== 'string') { return; }
    const trimmed = value.replace(/^\s+/, '');
    if (trimmed === value) { return; }
    this.setValueAndSyncModel(trimmed, true);
  }

  /**
   * A programmatic control write must update the model too. Formly's registerControl()
   * re-patches — with events — any control whose value differs from the model when the
   * form rebuilds, and that fires this field's change handler, which wipes a freshly-set
   * errorText. Mirrors the setValue + model-write pairing the panels already use when
   * they recalculate alert amounts.
   */
  private setValueAndSyncModel(value: string, emitEvent: boolean = false): void {
    this.formControl.setValue(value, { emitEvent });
    const key = this.field?.key;
    if (typeof key === 'string' && this.field?.model) {
      this.field.model[key] = value;
    }
  }

  ngDoCheck() {
    this.syncErrorState();
    this.syncStateFromTemplateOptions();
  }

  /** templateOptions are copied into textboxConfig once in ngOnInit, so screens that flip
   *  disabled/required/label at runtime need the config re-synced or the control keeps
   *  rendering its initial state. */
  private syncStateFromTemplateOptions(): void {
    const to = this.field?.templateOptions;
    if (!to) {
      return;
    }
    if (this.textboxConfig.disabled !== to.disabled
      || this.textboxConfig.required !== to.required
      || this.textboxConfig.label !== to.label) {
      this.textboxConfig = {
        ...this.textboxConfig,
        disabled: to.disabled,
        required: to.required,
        label: to.label
      };
    }
  }

  /** Ported from the legacy `text-input` control so converted screens keep their character
   *  restrictions. ds-textbox re-emits the native keypress, so blocking means preventDefault. */
  onKeypress(event: KeyboardEvent) {
    if (!this.allowKeypress(event)) {
      event.preventDefault();
    }
  }

  private allowKeypress(event: KeyboardEvent): boolean {
    const to = this.field?.templateOptions;
    if (!to) {
      return true;
    }
    if (to.omitSpecialChar) {
      const k = (event as any).charCode;
      return (k > 64 && k < 91) || (k > 96 && k < 123) || k == 8 || k == 32;
    }
    if (to.alphaNumericOnly) {
      return this.testChar(event, to.allowSpecialChar
        ? "^[ A-Za-z0-9_~`!$%^*(){};:\"'<>,@./#&+-]*$"
        : "^[a-zA-Z0-9 ]+$");
    }
    if (to.allowAcuteChar) {
      return this.testChar(event, to.allowSpecialChar
        ? "^[ A-Za-z_~`!$%^*(){};:\"'<>,@./#&+-]*$"
        : "^[a-zA-ZÀ-ú\p{L} '-]+$");
    }
    if (to.allowOnlyNumeric) {
      return this.testChar(event, to.allowSpecialChar
        ? "^[ 0-9_~`!$%^*(){};:\"'<>,@./#&+-]*$"
        : "^[0-9 ]+$");
    }
    if (to.alphaNumericSpecialWithoutComma) {
      return this.testChar(event, "^[ A-Za-z0-9_~`!$%^*(){};:\"'<>@./#&+-]*$");
    }
    return true;
  }

  private testChar(e: KeyboardEvent, pattern: string): boolean {
    const charCode = (e as any).charCode;
    const str = String.fromCharCode(!charCode ? (e as any).which : charCode);
    return new RegExp(pattern).test(str);
  }

  /**
   * The single owner of helperText + isInvalid. Nothing else may write that pair: a
   * second writer running later in the same tick overwrites the message this computed
   * before the bindings are evaluated, and the error never reaches the DOM.
   *
   * Three error sources, in precedence order:
   *   1. templateOptions.errorText - cross-field errors the panels write on save.
   *   2. a failed validator, once the form has been submitted.
   *   3. templateOptions.isInvalid - screens that drive their own errors through
   *      templateOptions, setting helperText and isInvalid together (category-panel's
   *      server-side field errors). With no error, that same helperText is the plain
   *      hint, which is why it also overrides the value captured in ngOnInit.
   */
  private syncErrorState(): void {
    const to = this.field.templateOptions;
    const errorText = to?.errorText;
    const externalHelperText = to?.helperText ?? this.originalHelperText;

    let newIsInvalid = false;
    let newHelperText = externalHelperText;

    if (errorText) {
      newIsInvalid = true;
      newHelperText = errorText;
    } else if (this.isFormSubmitted && this.formControl.invalid) {
      newIsInvalid = true;
      if (this.formControl.errors?.required || this.formControl.errors?.whitespace) {
        newHelperText = "Required";
      }
      else if(this.formControl.errors?.invalidEmail) {
        newHelperText = "Invalid email";
      }
    } else if (to?.isInvalid) {
      newIsInvalid = true;
    }

    if (this.textboxConfig.isInvalid !== newIsInvalid || this.textboxConfig.helperText !== newHelperText) {
      this.textboxConfig = {
        ...this.textboxConfig,
        isInvalid: newIsInvalid,
        helperText: newHelperText,
        value: this.formControl.value ?? ''
      };
    }
  }

  ngOnDestroy() {
    if (this.formSubmissionSubscription) {
      this.formSubmissionSubscription.unsubscribe();
    }
  }

  intizerOnly(event: any) {
    if (this.field.props.intizerOnly) {
      const regex = /^[0-9]$/;
      let str = String.fromCharCode(!event.charCode ? event.which : event.charCode);
      if (regex.test(str)) {
        this.textboxConfig = { ...this.textboxConfig, helperText: '' };
        return true;
      } else {
        this.textboxConfig = { ...this.textboxConfig, helperText: 'Enter numbers only' };
        return false;
      }
    }
  }
}